import type { EyeTraxPoint } from '../types';

export type RoiBin = {
  totalFrames: number;
  validFrames: number;
  centerFrames: number;
  sumAbsX: number;
  sumAbsY: number;
  sumAbsDX: number;
  sumAbsDY: number;
};

export type RoiSummaryV1 = {
  version: 1;
  totalFrames: number;
  validFrames: number;
  centerFrames: number;
  sumAbsX: number;
  sumAbsY: number;
  sumAbsDX: number;
  sumAbsDY: number;
  byMediaIndex: Record<number, RoiBin>;
};

export type RoiAccumulator = {
  version: 1;
  totalFrames: number;
  validFrames: number;
  centerFrames: number;
  sumAbsX: number;
  sumAbsY: number;
  sumAbsDX: number;
  sumAbsDY: number;
  byMediaIndex: Map<number, RoiBin>;
  lastGaze: { x: number; y: number } | null;
};

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

const safeGet = (points: EyeTraxPoint[], idx: number): EyeTraxPoint | null => {
  const p = points[idx];
  if (!p || typeof p.x !== 'number' || typeof p.y !== 'number') return null;
  return p;
};

const avgPoints = (points: EyeTraxPoint[], indices: number[]): { x: number; y: number } | null => {
  let sx = 0;
  let sy = 0;
  let n = 0;
  for (const i of indices) {
    const p = safeGet(points, i);
    if (!p) continue;
    sx += p.x;
    sy += p.y;
    n += 1;
  }
  if (!n) return null;
  return { x: sx / n, y: sy / n };
};

function eyeOffset(
  points: EyeTraxPoint[],
  irisIdx: number[],
  cornerA: number,
  cornerB: number,
  topIdx: number,
  bottomIdx: number,
): { x: number; y: number } | null {
  const iris = avgPoints(points, irisIdx);
  const a = safeGet(points, cornerA);
  const b = safeGet(points, cornerB);
  const t = safeGet(points, topIdx);
  const bo = safeGet(points, bottomIdx);
  if (!iris || !a || !b || !t || !bo) return null;

  const cx = (a.x + b.x) / 2;
  const cy = (t.y + bo.y) / 2;
  const w = Math.abs(b.x - a.x);
  const h = Math.abs(bo.y - t.y);
  if (w < 1e-6 || h < 1e-6) return null;

  const rx = (iris.x - cx) / w;
  const ry = (iris.y - cy) / h;
  return { x: clamp(rx * 2, -1, 1), y: clamp(ry * 2, -1, 1) };
}

export function computeRelativeGaze(points: EyeTraxPoint[]): { x: number; y: number } | null {
  // FaceLandmarker commonly returns 478 landmarks when iris refinement is present.
  if (!Array.isArray(points) || points.length < 478) return null;

  // These indices follow MediaPipe FaceMesh + iris refinement.
  const left = eyeOffset(points, [468, 469, 470, 471, 472], 33, 133, 159, 145);
  const right = eyeOffset(points, [473, 474, 475, 476, 477], 362, 263, 386, 374);
  if (!left && !right) return null;

  const xs: number[] = [];
  const ys: number[] = [];
  if (left) {
    xs.push(left.x);
    ys.push(left.y);
  }
  if (right) {
    xs.push(right.x);
    ys.push(right.y);
  }
  return {
    x: clamp(xs.reduce((a, b) => a + b, 0) / xs.length, -1, 1),
    y: clamp(ys.reduce((a, b) => a + b, 0) / ys.length, -1, 1),
  };
}

export function createRoiAccumulator(): RoiAccumulator {
  return {
    version: 1,
    totalFrames: 0,
    validFrames: 0,
    centerFrames: 0,
    sumAbsX: 0,
    sumAbsY: 0,
    sumAbsDX: 0,
    sumAbsDY: 0,
    byMediaIndex: new Map(),
    lastGaze: null,
  };
}

function getBin(acc: RoiAccumulator, mediaIndex: number): RoiBin {
  const existing = acc.byMediaIndex.get(mediaIndex);
  if (existing) return existing;
  const created: RoiBin = {
    totalFrames: 0,
    validFrames: 0,
    centerFrames: 0,
    sumAbsX: 0,
    sumAbsY: 0,
    sumAbsDX: 0,
    sumAbsDY: 0,
  };
  acc.byMediaIndex.set(mediaIndex, created);
  return created;
}

export function updateRoiAccumulator(
  acc: RoiAccumulator,
  mediaIndex: number,
  points: EyeTraxPoint[],
  opts?: { centerThreshold?: number },
): void {
  const centerThreshold = typeof opts?.centerThreshold === 'number' ? opts.centerThreshold : 0.35;
  acc.totalFrames += 1;

  const bin = getBin(acc, mediaIndex);
  bin.totalFrames += 1;

  const gaze = computeRelativeGaze(points);
  if (!gaze) {
    acc.lastGaze = null;
    return;
  }

  acc.validFrames += 1;
  bin.validFrames += 1;

  const absX = Math.abs(gaze.x);
  const absY = Math.abs(gaze.y);

  acc.sumAbsX += absX;
  acc.sumAbsY += absY;
  bin.sumAbsX += absX;
  bin.sumAbsY += absY;

  const isCenter = absX <= centerThreshold && absY <= centerThreshold;
  if (isCenter) {
    acc.centerFrames += 1;
    bin.centerFrames += 1;
  }

  if (acc.lastGaze) {
    const dx = Math.abs(gaze.x - acc.lastGaze.x);
    const dy = Math.abs(gaze.y - acc.lastGaze.y);
    acc.sumAbsDX += dx;
    acc.sumAbsDY += dy;
    bin.sumAbsDX += dx;
    bin.sumAbsDY += dy;
  }
  acc.lastGaze = gaze;
}

export function finalizeRoiAccumulator(acc: RoiAccumulator): RoiSummaryV1 {
  const byMediaIndex: Record<number, RoiBin> = {};
  for (const [k, v] of acc.byMediaIndex.entries()) {
    byMediaIndex[k] = v;
  }
  return {
    version: 1,
    totalFrames: acc.totalFrames,
    validFrames: acc.validFrames,
    centerFrames: acc.centerFrames,
    sumAbsX: acc.sumAbsX,
    sumAbsY: acc.sumAbsY,
    sumAbsDX: acc.sumAbsDX,
    sumAbsDY: acc.sumAbsDY,
    byMediaIndex,
  };
}
